import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Edit, Trash2, Plus, X, GripVertical, Globe } from 'lucide-react';
import toast from 'react-hot-toast';

interface FAQ {
  id: string;
  service_id?: string;
  city_id?: string;
  question: string;
  answer: string;
  order: number;
}

interface Service {
  id: string;
  title: string;
}

interface City {
  id: string;
  name: string;
  state: {
    code: string;
  };
}

export function FAQManagement() {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState<Partial<FAQ> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultFaq = {
    question: '',
    answer: '',
    service_id: '',
    city_id: '',
    order: 0
  };

  useEffect(() => {
    Promise.all([
      loadFaqs(),
      loadServices(),
      loadCities()
    ]).then(() => setLoading(false));
  }, []);

  const loadFaqs = async () => {
    try {
      const { data, error } = await supabase
        .from('faqs')
        .select(`
          *,
          service:service_id(title),
          city:city_id(name, state:state_id(code))
        `)
        .order('order');

      if (error) throw error;
      setFaqs(data || []);
    } catch (error: any) {
      toast.error('Failed to load FAQs: ' + error.message);
    }
  };

  const loadServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('id, title')
        .order('title');

      if (error) throw error;
      setServices(data || []);
    } catch (error: any) {
      toast.error('Failed to load services: ' + error.message);
    }
  };

  const loadCities = async () => {
    try {
      const { data, error } = await supabase
        .from('cities')
        .select(`
          id,
          name,
          state:state_id(code)
        `)
        .order('name');

      if (error) throw error;
      setCities(data || []);
    } catch (error: any) {
      toast.error('Failed to load cities: ' + error.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFaq) return;

    setIsSubmitting(true);
    try {
      if (editingFaq.id) {
        // Update existing FAQ
        const { error } = await supabase
          .from('faqs')
          .update({
            question: editingFaq.question,
            answer: editingFaq.answer,
            order: editingFaq.order,
            service_id: editingFaq.service_id || null,
            city_id: editingFaq.city_id || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingFaq.id);

        if (error) throw error;
        toast.success('FAQ updated successfully');
      } else {
        // Create new FAQ
        const { error } = await supabase
          .from('faqs')
          .insert([{
            question: editingFaq.question,
            answer: editingFaq.answer,
            order: faqs.length,
            service_id: editingFaq.service_id || null,
            city_id: editingFaq.city_id || null
          }]);

        if (error) throw error;
        toast.success('FAQ added successfully');
      }

      setShowForm(false);
      setEditingFaq(null);
      loadFaqs();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      toast.error('Failed to save FAQ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      const { error } = await supabase
        .from('faqs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('FAQ deleted successfully');
      loadFaqs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast.error('Failed to delete FAQ');
    }
  };

  const handleReorder = async (draggedId: string, targetId: string) => {
    const draggedIndex = faqs.findIndex(faq => faq.id === draggedId);
    const targetIndex = faqs.findIndex(faq => faq.id === targetId);
    
    if (draggedIndex === -1 || targetIndex === -1) return;

    try {
      // Create new array with updated order
      const newFaqs = [...faqs];
      const [draggedFaq] = newFaqs.splice(draggedIndex, 1);
      newFaqs.splice(targetIndex, 0, draggedFaq);

      // Update order in state
      setFaqs(newFaqs.map((faq, index) => ({ ...faq, order: index })));

      // Update order in database
      const { error } = await supabase
        .from('faqs')
        .upsert(
          newFaqs.map((faq, index) => ({
            id: faq.id,
            order: index
          }))
        );

      if (error) throw error;
    } catch (error) {
      console.error('Error reordering FAQs:', error);
      toast.error('Failed to reorder FAQs');
      loadFaqs(); // Reload original order
    }
  };

  if (loading) {
    return <div>Loading FAQs...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Manage FAQs</h2>
        <button
          onClick={() => {
            setEditingFaq({
              id: '',
              question: '',
              answer: '',
              order: faqs.length
            });
            setShowForm(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add FAQ
        </button>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={faq.id}
            className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
            draggable
            onDragStart={(e) => e.dataTransfer.setData('text/plain', faq.id)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const draggedId = e.dataTransfer.getData('text/plain');
              handleReorder(draggedId, faq.id);
            }}
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center">
                  <GripVertical className="w-5 h-5 text-gray-400 cursor-move mr-2" />
                  <div>
                    <h5 className="font-medium text-gray-900">
                      {faq.question}
                      {!faq.service_id && !faq.city_id && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <Globe className="w-3 h-3 mr-1" />
                          Global
                        </span>
                      )}
                    </h5>
                    {(faq.service || faq.city) && (
                      <p className="text-sm text-gray-500 mt-1">
                        {faq.service ? `Service: ${faq.service.title}` : ''}
                        {faq.city ? `Location: ${faq.city.name}, ${faq.city.state.code}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                <p className="mt-2 text-gray-600 whitespace-pre-wrap">{faq.answer}</p>
              </div>
              <div className="flex space-x-2 ml-4">
                <button
                  onClick={() => {
                    setEditingFaq(faq);
                    setShowForm(true);
                  }}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full"
                >
                  <Edit className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDelete(faq.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-full"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingFaq?.id ? 'Edit FAQ' : 'Add FAQ'}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingFaq(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Question
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Use {'{city}'}, {'{state}'}, or {'{state_code}'} as placeholders for dynamic content
                </p>
                <input
                  type="text"
                  value={editingFaq?.question || ''}
                  onChange={(e) => setEditingFaq(prev => ({ ...prev!, question: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                  placeholder="e.g., What plumbing services are available in {city}?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Answer
                </label>
                <p className="text-sm text-gray-500 mb-2">
                  Use {'{city}'}, {'{state}'}, or {'{state_code}'} as placeholders for dynamic content
                </p>
                <textarea
                  value={editingFaq?.answer || ''}
                  onChange={(e) => setEditingFaq(prev => ({ ...prev!, answer: e.target.value }))}
                  rows={6}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  required
                  placeholder="e.g., We offer comprehensive plumbing services in {city}, {state_code}..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Service (Optional)
                  </label>
                  <select
                    value={editingFaq?.service_id || ''}
                    onChange={(e) => setEditingFaq(prev => ({ 
                      ...prev!, 
                      service_id: e.target.value || null,
                      city_id: null // Clear city if service is selected
                    }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">All Services</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    City (Optional)
                  </label>
                  <select
                    value={editingFaq?.city_id || ''}
                    onChange={(e) => setEditingFaq(prev => ({ 
                      ...prev!, 
                      city_id: e.target.value || null,
                      service_id: null // Clear service if city is selected
                    }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">All Cities</option>
                    {cities.map(city => (
                      <option key={city.id} value={city.id}>
                        {city.name}, {city.state.code}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingFaq(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}